import { authClient } from '../utils/amplifyClient';
import { getGraphQlErrorMessage } from '../utils/graphqlErrorUtils';

export type CompanyUnitOfMeasureRecord = {
    id: string;
    companyId: string;
    name: string;
    value: string;
    sortOrder?: number | null;
};

function throwIfGraphQlErrors(result: { errors?: unknown }, fallback: string) {
    const errors = result.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        throw new Error(getGraphQlErrorMessage(result) || fallback);
    }
}

function toRecord(item: {
    id: string;
    companyId: string;
    name: string;
    value: string;
    sortOrder?: number | null;
}): CompanyUnitOfMeasureRecord {
    return {
        id: item.id,
        companyId: item.companyId,
        name: item.name,
        value: item.value,
        sortOrder: item.sortOrder ?? null,
    };
}

export async function listCompanyUnitsOfMeasureByCompanyId(
    companyId: string,
): Promise<CompanyUnitOfMeasureRecord[]> {
    const allItems: CompanyUnitOfMeasureRecord[] = [];
    const result = await authClient.models.CompanyUnitOfMeasure.listCompanyUnitOfMeasureByCompanyId({
        companyId,
    });
    throwIfGraphQlErrors(result, 'Failed to load units of measure');
    for (const item of result.data ?? []) {
        if (item) allItems.push(toRecord(item));
    }

    return allItems.sort((a, b) => {
        const orderA = a.sortOrder ?? 9999;
        const orderB = b.sortOrder ?? 9999;
        if (orderA !== orderB) return orderA - orderB;
        return a.name.localeCompare(b.name);
    });
}

export async function createCompanyUnitOfMeasure(input: {
    companyId: string;
    name: string;
    value: string;
    sortOrder?: number;
}): Promise<CompanyUnitOfMeasureRecord> {
    const result = await authClient.models.CompanyUnitOfMeasure.create(input);
    throwIfGraphQlErrors(result, 'Failed to create unit of measure');
    if (!result.data) {
        throw new Error('Failed to create unit of measure');
    }
    return toRecord(result.data);
}

export async function updateCompanyUnitOfMeasure(input: {
    id: string;
    name?: string;
    value?: string;
    sortOrder?: number | null;
}): Promise<CompanyUnitOfMeasureRecord> {
    const result = await authClient.models.CompanyUnitOfMeasure.update(input);
    throwIfGraphQlErrors(result, 'Failed to update unit of measure');
    if (!result.data) {
        throw new Error('Failed to update unit of measure');
    }
    return toRecord(result.data);
}

export async function deleteCompanyUnitOfMeasure(id: string): Promise<void> {
    const result = await authClient.models.CompanyUnitOfMeasure.delete({ id });
    throwIfGraphQlErrors(result, 'Failed to delete unit of measure');
}
